import { LineItem, Package, Plan } from '@/common/models';
import NumberHelper from '@/utils/NumberHelper';
import { mapOrgAppActions } from '@/utils/StateMapping';

interface IAvailsData {
  deliveredImpressions: number;
  deliveryGoal: number;
  estimatedTotalImpressionsCapacity: number;
}

export abstract class EntityAvailsDataHelper {
  constructor(protected entity: LineItem | Plan | Package) {}

  protected abstract fetchDeliveredImpressions(): Promise<number>;
  protected abstract fetchDeliveryGoal(): Promise<number>;
  protected abstract getCPM(): Promise<number>;

  protected getCPMForTargets(): Promise<{ average_cpm: number }> {
    const { getCPMForTargets } = mapOrgAppActions();
    return getCPMForTargets(this.entity.cpmTargetParams);
  }

  protected getEstimatedDeliveryStatistics(): Promise<{ impressions: number }> {
    const { getEstimatedDeliveryStatistics } = mapOrgAppActions();
    const targetParams = this.entity.targetParamsEstimatedDeliveryStatistics;
    return getEstimatedDeliveryStatistics({ params: targetParams, cancelTokenSource: null });
  }

  protected async fetchTotalImpressionsCapacity(): Promise<number> {
    try {
      const audienceInfo = await this.getEstimatedDeliveryStatistics();
      return audienceInfo.impressions;
    } catch (error) {
      console.error(error);
      return 0;
    }
  }

  protected async computeDeliveryGoalFromCpm(budget: number) {
    const cpm = await this.getCPM();
    if (NumberHelper.isSafeDivider(cpm)) {
      return (budget / cpm) * 1000;
    } else {
      return 0;
    }
  }

  public async fetchAvailsData(): Promise<IAvailsData> {
    const [deliveredImpressions, deliveryGoal, estimatedTotalImpressionsCapacity] = await Promise.all([
      this.fetchDeliveredImpressions(),
      this.fetchDeliveryGoal(),
      this.fetchTotalImpressionsCapacity(),
    ]);

    return {
      deliveredImpressions,
      deliveryGoal,
      estimatedTotalImpressionsCapacity,
    };
  }
}
